import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Language as CorsIcon,
  LockOutlined as LockIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import settingsService, { CorsOrigin } from '../../../services/settings.service';

const fonteBadge = (fonte: CorsOrigin['fonte']) => {
  if (fonte === 'sistema') return <Chip label="Sistema" size="small" color="default" icon={<LockIcon />} />;
  if (fonte === 'env')     return <Chip label="Config (.env)" size="small" color="primary" variant="outlined" icon={<LockIcon />} />;
  return                          <Chip label="Personalizado" size="small" color="success" variant="outlined" />;
};

const CorsOriginsPage: React.FC = () => {
  const [origins, setOrigins] = useState<CorsOrigin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialog, setAddDialog] = useState(false);
  const [delDialog, setDelDialog] = useState<CorsOrigin | null>(null);
  const [saving, setSaving] = useState(false);
  const [formOrigin, setFormOrigin] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const fetchOrigins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await settingsService.getCorsOrigins();
      setOrigins(res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar origins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrigins(); }, [fetchOrigins]);

  const handleAdd = async () => {
    setFormError(null);
    if (!formOrigin.trim()) { setFormError('Origin é obrigatório'); return; }
    try { new URL(formOrigin.trim()); } catch {
      setFormError('URL inválida — deve incluir protocolo, ex: https://exemplo.cv');
      return;
    }
    setSaving(true);
    try {
      const res = await settingsService.addCorsOrigin(formOrigin.trim(), formDesc.trim() || undefined);
      setOrigins(prev => [...prev, res.data]);
      setAddDialog(false);
      setFormOrigin('');
      setFormDesc('');
    } catch (err: any) {
      setFormError(err.response?.data?.error || err.message || 'Erro ao adicionar origin');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (origin: CorsOrigin) => {
    if (!origin.id) return;
    try {
      const res = await settingsService.toggleCorsOrigin(origin.id);
      setOrigins(prev => prev.map(o => o.id === origin.id ? { ...o, ativo: res.data.ativo } : o));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao atualizar');
    }
  };

  const handleDelete = async () => {
    if (!delDialog?.id) return;
    setSaving(true);
    try {
      await settingsService.deleteCorsOrigin(delDialog.id);
      setOrigins(prev => prev.filter(o => o.id !== delDialog.id));
      setDelDialog(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao remover origin');
    } finally {
      setSaving(false);
    }
  };

  const total = origins.length;
  const ativos = origins.filter(o => o.ativo).length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CorsIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>CORS Origins</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setFormOrigin(''); setFormDesc(''); setFormError(null); setAddDialog(true); }}>
          Adicionar Origin
        </Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <InfoIcon fontSize="small" color="info" sx={{ mt: '2px' }} />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Define os domínios autorizados a fazer pedidos à API Pagali. Alterações na BD ficam activas em até 5 minutos.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Chip label="Sistema" size="small" color="default" icon={<LockIcon />} />
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Hardcoded (SISP/Vinti4)</Typography>
                <Chip label="Config (.env)" size="small" color="primary" variant="outlined" icon={<LockIcon />} />
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Ficheiro de configuração — requer redeploy</Typography>
                <Chip label="Personalizado" size="small" color="success" variant="outlined" />
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Gerido pelo Admin — activo em 5 min</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Total: <strong>{total}</strong> origens &nbsp;·&nbsp; Activas: <strong>{ativos}</strong>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Origin (URL)</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Fonte</TableCell>
              <TableCell>Criado por</TableCell>
              <TableCell align="center">Activo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : origins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma origin configurada
                </TableCell>
              </TableRow>
            ) : origins.map((o, idx) => (
              <TableRow key={o.id ?? `static-${idx}`} hover sx={{ opacity: o.ativo ? 1 : 0.5 }}>
                <TableCell>
                  <Chip
                    label={o.origin}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                </TableCell>
                <TableCell>{o.descricao || '—'}</TableCell>
                <TableCell>{fonteBadge(o.fonte)}</TableCell>
                <TableCell>{o.criado_por || '—'}</TableCell>
                <TableCell align="center">
                  {o.fonte === 'bd' ? (
                    <Switch checked={!!o.ativo} onChange={() => handleToggle(o)} size="small" />
                  ) : (
                    <Tooltip title="Gerido fora do Admin">
                      <Switch checked disabled size="small" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  {o.fonte === 'bd' ? (
                    <Tooltip title="Remover">
                      <IconButton size="small" color="error" onClick={() => setDelDialog(o)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Não pode ser removido pelo Admin">
                      <span>
                        <IconButton size="small" disabled>
                          <LockIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar CORS Origin</DialogTitle>
        <DialogContent>
          <TextField
            label="Origin (URL completa)"
            placeholder="https://exemplo.cv"
            value={formOrigin}
            onChange={e => setFormOrigin(e.target.value)}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            error={!!formError}
            helperText={formError || 'Incluir protocolo https:// e sem barra no final'}
          />
          <TextField
            label="Descrição (opcional)"
            placeholder="ex: Portal do parceiro X"
            value={formDesc}
            onChange={e => setFormDesc(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleAdd} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!delDialog} onClose={() => setDelDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover Origin</DialogTitle>
        <DialogContent>
          <Typography>Tens a certeza que queres remover <strong>{delDialog?.origin}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelDialog(null)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CorsOriginsPage;
